import React from "react";
import styled from "styled-components";
import { Link } from "@inertiajs/react";
import {
  brand_orange,
  brand_orange_deep,
  brand_blue,
  brand_success,
  brand_danger,
} from "../../constants/styles";
import PagedCarousel from "../Carousel";
import SectionShell, { SectionTitle, SectionSubtitle } from "../SectionShell";
import usePagedSection from "../usePagedSection";
import { Skeleton } from "../Skeleton";
import SeeAllSectionLink from "../SeeAllSectionLink";
import { apiGet } from "../../api";
import useReducedMotion from "../../hooks/useReducedMotion";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";

const Card = styled.article`
  background: #f9fafb;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition:
    transform 200ms ease,
    box-shadow 200ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  }

  &[data-theme="dark"],
  [data-theme="dark"] & {
    background: #17171e;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);

    &:hover {
      background: #1f2937;
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
    }
  }
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
`;

const StatusLabel = styled.span`
  color: ${({ $status }) =>
    $status === "selesai" ? brand_danger : brand_success};
  text-transform: capitalize;
`;

const CapacityBadge = styled.span`
  background: #fff;
  border: 1px solid ${brand_orange};
  color: ${brand_orange};
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
`;

const AvatarWrap = styled.div`
  position: relative;
  background: ${({ $status }) =>
    $status === "selesai" ? "#93c5fd" : brand_orange};
  border-radius: 12px;
  aspect-ratio: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 600ms ease;
`;

const AvatarName = styled.div`
  position: relative;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  width: 100%;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 4px;
  z-index: 1;
`;

// Cross-fading slideshow for the webinar card cover. Cycles through every
// available image (webinar banner + speaker avatars) every 4 seconds.
// 4s keeps each image on-screen long enough to register without being a
// slideshow stuck on one frame; slower (6s+) makes the card feel stale,
// faster (<3s) feels twitchy and fights reading time.
const COVER_ROTATE_MS = 4000;

export function WebinarCover({ images, speaker, status }) {
  const [idx, setIdx] = React.useState(0);
  const imgs = Array.isArray(images) ? images.filter(Boolean) : [];
  const reducedMotion = useReducedMotion();

  React.useEffect(() => {
    if (imgs.length <= 1) return undefined;
    if (reducedMotion) return undefined;
    const t = setInterval(
      () => setIdx((i) => (i + 1) % imgs.length),
      COVER_ROTATE_MS,
    );
    return () => clearInterval(t);
  }, [imgs.length, reducedMotion]);

  return (
    <AvatarWrap $status={status}>
      {imgs.map((src, i) => (
        <AvatarImage
          key={src}
          src={src}
          alt={speaker || ""}
          loading={i === 0 ? "eager" : "lazy"}
          $active={i === idx}
          aria-hidden={i !== idx}
        />
      ))}
      <AvatarName>{speaker}</AvatarName>
    </AvatarWrap>
  );
}

const Meta = styled.div`
  font-size: 12px;
  color: #374151;
  line-height: 1.5;

  &[data-theme="dark"],
  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

// Transparency chips: which webinars the viewer has joined, which have a
// watchable recording, the access type, and whether a certificate is offered.
// Outlined-pill treatment matches the existing CapacityBadge aesthetic.
const TONES = {
  success: brand_success,
  info: brand_blue,
  special: "#7C3AED",
  warn: brand_orange_deep,
};

const WEBINAR_TYPE_CHIPS = {
  free: { label: "Gratis", tone: "success" },
  general: { label: "Umum", tone: "info" },
  special: { label: "Khusus", tone: "special" },
};

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 1px solid ${({ $tone }) => TONES[$tone] || brand_orange};
  color: ${({ $tone }) => TONES[$tone] || brand_orange};
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.6;
  white-space: nowrap;

  &[data-theme="dark"],
  [data-theme="dark"] & {
    background: transparent;
  }
`;

// Renders the chip row for a webinar card. Returns null when there is nothing
// worth surfacing, so cards without any signal stay clean.
export function WebinarChips(w) {
  const typeChip = WEBINAR_TYPE_CHIPS[w.webinar_type];
  if (!w.joined && !w.has_recording && !typeChip && !w.certificate_offered) {
    return null;
  }
  return (
    <ChipRow>
      {w.joined && (
        <Chip $tone="success" title="Anda sudah terdaftar di webinar ini">
          <span aria-hidden="true">✓</span> Terdaftar
        </Chip>
      )}
      {w.has_recording && (
        // States that a recording EXISTS, not that this viewer may watch it —
        // access still depends on registration (see WebinarRecording).
        <Chip $tone="info" title="Webinar ini memiliki rekaman">
          <span aria-hidden="true">▶</span> Rekaman
        </Chip>
      )}
      {typeChip && <Chip $tone={typeChip.tone}>{typeChip.label}</Chip>}
      {w.certificate_offered && (
        <Chip $tone="warn">
          <span aria-hidden="true">🎓</span> Sertifikat
        </Chip>
      )}
    </ChipRow>
  );
}

const MetaHeader = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
  min-height: 2.8em;

  &[data-theme="dark"],
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

// Inertia Link so navigating from a rebranded page (homepage carousel
// or WebinarRelated on the detail page) into another rebranded webinar
// detail page swaps the SPA component instead of triggering a full
// reload. Inertia gracefully falls back to a full navigation if the
// destination returns a non-Inertia response — matters when the
// `webinars_rebrand_v1` toggle is OFF for the destination user (legacy
// Slim show_v2 still serves correctly via full reload).
const Button = styled(Link)`
  display: inline-block;
  text-align: center;
  text-decoration: none;
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.05em;
  transition:
    filter 150ms ease,
    transform 100ms ease;
  background: ${({ $variant }) =>
    $variant === "secondary" ? brand_blue : brand_orange};

  &:hover {
    filter: brightness(0.95);
    color: #fff;
  }
  &:active {
    transform: scale(0.98);
  }
  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 2px;
  }
`;

export function renderWebinarSkeleton(i) {
  return (
    <Card key={`sk-${i}`} aria-hidden="true">
      <StatusRow>
        <Skeleton $width="64px" $height="14px" $radius="4px" />
        <Skeleton $width="82px" $height="20px" $radius="999px" />
      </StatusRow>
      <Skeleton $radius="12px" style={{ aspectRatio: "1" }} />
      <Meta>
        <Skeleton $width="45%" $height="14px" $radius="4px" />
        <div style={{ height: 8 }} />
        <Skeleton $width="95%" $height="12px" $radius="4px" />
        <div style={{ height: 4 }} />
        <Skeleton $width="70%" $height="12px" $radius="4px" />
      </Meta>
      <Meta>
        <Skeleton $width="55%" $height="12px" $radius="4px" />
        <div style={{ height: 4 }} />
        <Skeleton $width="40%" $height="12px" $radius="4px" />
      </Meta>
      <Skeleton $height="38px" $radius="8px" />
    </Card>
  );
}

export function renderWebinarCard(w) {
  const images =
    w.cover_images && w.cover_images.length > 0
      ? w.cover_images
      : w.speaker_avatar_url
        ? [w.speaker_avatar_url]
        : [];
  return (
    <Card key={w.id}>
      <StatusRow>
        <StatusLabel $status={w.status}>{w.status}</StatusLabel>
        <CapacityBadge>{w.capacity_label}</CapacityBadge>
      </StatusRow>
      <WebinarCover images={images} speaker={w.speaker} status={w.status} />
      <Meta>
        <MetaHeader>{w.title}</MetaHeader>
        <div>{`${w.category} - ${w.topic}`}</div>
      </Meta>
      {WebinarChips(w)}
      <Meta>
        <div>📅 {w.date}</div>
        <div>🕐 {w.time}</div>
      </Meta>
      <Button
        href={w.detail_url || (w.slug ? `/webinars/${w.slug}` : "#")}
        $variant={w.cta_variant}
        aria-label={`${w.cta_label}: ${w.topic || w.category}`}
      >
        {w.cta_label}
      </Button>
    </Card>
  );
}

// --- Grid + subgrid card -------------------------------------------------
// Shared by the /webinars listing AND the homepage carousel. Each card is a
// SUBGRID of its parent grid (6 shared row tracks), so status, image, title,
// category, chips and date line up across a whole row of cards regardless of
// how long any single title or chip set is.
//
// The card itself is the link — there is no separate DETAIL button. The anchor
// has to BE the grid item (not wrap one), otherwise it breaks the subgrid
// chain. Nothing else inside the card is interactive, so there is no nested
// interactive-element hazard.
const GridCard = styled(Link)`
  display: grid;
  grid-row: span 6;
  grid-template-rows: subgrid;
  row-gap: 12px;
  background: #f9fafb;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition:
    transform 200ms ease,
    box-shadow 200ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
    color: inherit;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 3px solid ${brand_orange};
    outline-offset: 3px;
  }

  &[data-theme="dark"],
  [data-theme="dark"] & {
    background: #17171e;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);

    &:hover {
      background: #1f2937;
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
    }
  }

  /* Graceful fallback where subgrid is unsupported: a normal stacked card. */
  @supports not (grid-template-rows: subgrid) {
    grid-row: auto;
    grid-template-rows: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  color: #1f2937;
  line-height: 1.35;
  /* Clamp to 2 lines + ellipsis so a long title never blows out the row. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &[data-theme="dark"],
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const CardCategory = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #6b7280;
  /* Keep the "category - tema" label to one tidy line. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

// Always rendered so the chips track stays occupied even when a card has no
// chips — otherwise the date row would shift up and break alignment.
const ChipsCell = styled.div`
  min-height: 0;
`;

const CardDate = styled.div`
  align-self: end;
  font-size: 12px;
  color: #374151;
  line-height: 1.6;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

// Six direct children map 1:1 to the six shared subgrid rows.
export function WebinarGridCard(w) {
  const images =
    w.cover_images && w.cover_images.length > 0
      ? w.cover_images
      : w.speaker_avatar_url
        ? [w.speaker_avatar_url]
        : [];
  const heading = w.title || w.topic || w.category;
  const meta = w.topic ? `${w.category} - ${w.topic}` : w.category;
  return (
    <GridCard
      key={w.id}
      href={w.detail_url || (w.slug ? `/webinars/${w.slug}` : "#")}
      // Without this the link's accessible name would be the card's entire
      // text content — status, capacity, speaker (twice), chips and emoji.
      aria-label={`${heading} — ${w.date}`}
    >
      <StatusRow>
        <StatusLabel $status={w.status}>{w.status}</StatusLabel>
        <CapacityBadge>{w.capacity_label}</CapacityBadge>
      </StatusRow>
      <WebinarCover images={images} speaker={w.speaker} status={w.status} />
      <CardTitle title={heading}>{heading}</CardTitle>
      <CardCategory title={meta}>{meta}</CardCategory>
      <ChipsCell>{WebinarChips(w)}</ChipsCell>
      <CardDate>
        <span aria-hidden="true">📅</span> {w.date} ·{" "}
        <span aria-hidden="true">🕐</span> {w.time}
      </CardDate>
    </GridCard>
  );
}

// Skeleton with the same six rows so loading placeholders align on the grid
// and don't jump when real cards arrive.
export function WebinarGridSkeleton(i) {
  return (
    <GridCard key={`sk-${i}`} as="div" aria-hidden="true">
      <StatusRow>
        <Skeleton $width="64px" $height="14px" $radius="4px" />
        <Skeleton $width="82px" $height="20px" $radius="999px" />
      </StatusRow>
      <Skeleton $radius="12px" style={{ aspectRatio: "1" }} />
      <Skeleton $width="90%" $height="18px" $radius="4px" />
      <Skeleton $width="40%" $height="12px" $radius="4px" />
      <Skeleton $width="60%" $height="18px" $radius="999px" />
      <Skeleton $width="70%" $height="12px" $radius="4px" />
    </GridCard>
  );
}

function WebinarCarousel({ webinarsIndexEnabled = false }) {
  // Anonymous visitors get a capped carousel (see
  // Webinars::CatalogVisibility.homepage_max_items). `loading` gates the hint
  // so it never flashes before auth state is known.
  const auth = React.use(LoggedInUserContext);
  const showAnonymousHint = Boolean(auth && !auth.loading && !auth.signedIn);

  // Rails uses 1-based `page`; hook uses 0-based `pageIdx`.
  const loader = React.useCallback(async (pageIdx, perPage) => {
    const { parsedResponse } = await apiGet(
      `/api/v1/homepage/webinars?page=${pageIdx + 1}&per_page=${perPage}`,
    );
    return {
      items: parsedResponse?.data ?? [],
      totalCount: parsedResponse?.meta?.total ?? 0,
    };
  }, []);

  const { items, pageIdx, setPageIdx, totalPages, loading, perPage } =
    usePagedSection({ loader });

  return (
    <SectionShell id="webinar">
      <SectionTitle>Webinar dan Live Online Classes</SectionTitle>
      <SectionSubtitle>
        Jangan Sampai Terlewatkan Kesempatan Untuk Konsultasi Dan Diskusi
        Interaktif Bersama Mentor-Mentor Praktisi Dan Akademisi Berpengalaman
        Kami
      </SectionSubtitle>
      <PagedCarousel
        items={items}
        perPage={perPage}
        pageIdx={pageIdx}
        totalPages={totalPages}
        onPageChange={setPageIdx}
        loading={loading}
        renderItem={WebinarGridCard}
        renderSkeleton={WebinarGridSkeleton}
        subgridRows={6}
        ariaLabel="Webinar carousel"
        emptyMessage="Belum ada webinar yang tersedia saat ini."
      />
      {webinarsIndexEnabled && (
        <SeeAllSectionLink
          href="/webinars"
          label="Lihat Semua Webinar dan Live Online Classes"
          ariaLabel="Lihat semua webinar dan live online classes"
          hint={
            showAnonymousHint ? (
              <>
                Menampilkan sebagian webinar.{" "}
                <a href="/login?redirect_after_login=%2Fwebinars">Masuk</a>{" "}
                untuk melihat seluruh katalog.
              </>
            ) : null
          }
        />
      )}
    </SectionShell>
  );
}

export default React.memo(WebinarCarousel);
